import { useCallback, useState, type FormEvent, type JSX } from "react";
import { cn } from "@openreel/ui";
import { StepShell } from "./StepShell";
import {
  FIELD_BASE,
  FIELD_FOCUS,
  FIELD_INPUT,
  FIELD_MOTION,
  FOCUS_RING,
  PILL_BASE,
  PILL_DIMMED,
  PILL_LIGHT,
  PILL_MOTION,
} from "./surface";
import type { AuthUser } from "./types";

const SAVE_ERROR = "We could not save that. Check your connection and try again.";

export interface NameStepProps {
  /** Drawn top-right in the shell; the name field starts from whatever it holds. */
  user: AuthUser | null;
  /**
   * Persists the name via PATCH /auth/profile. `lastName` is null when the
   * single field is left blank. Rejects so this step can hold and show an error
   * rather than advancing on a failed save.
   */
  onSave: (firstName: string, lastName: string | null) => Promise<void>;
}

/**
 * The first thing a brand-new account is asked. Onboarding answers come next and
 * go to a different endpoint, so the name lives on its own step here rather than
 * inside the wizard's reducer. A returning account already has a name and never
 * sees this.
 */
function labelClass(): string {
  return cn(FIELD_BASE, FIELD_MOTION, FIELD_FOCUS, "flex items-center");
}

const inputClass = FIELD_INPUT;

export function NameStep({ user, onSave }: NameStepProps): JSX.Element {
  const [first, setFirst] = useState(user?.firstName ?? "");
  const [last, setLast] = useState(user?.lastName ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedFirst = first.trim();
  const ready = trimmedFirst.length > 0;

  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (busy || !ready) return;

      setBusy(true);
      setError(null);
      try {
        await onSave(trimmedFirst, last.trim() ? last.trim() : null);
      } catch {
        setError(SAVE_ERROR);
      } finally {
        setBusy(false);
      }
    },
    [busy, last, onSave, ready, trimmedFirst],
  );

  return (
    <StepShell
      kicker="First, the basics"
      heading="What's your name?"
      subheading="This is how flshy will greet you."
      user={user}
      error={error}
      announcement="First, the basics. What's your name?"
      footer={
        <button
          type="submit"
          form="name-step-form"
          disabled={!ready || busy}
          className={cn(
            PILL_BASE,
            FOCUS_RING,
            "px-6",
            !ready || busy ? PILL_DIMMED : cn(PILL_MOTION, PILL_LIGHT),
          )}
        >
          {busy ? "Saving..." : "Continue"}
        </button>
      }
    >
      <form
        id="name-step-form"
        onSubmit={submit}
        className="flex w-full max-w-md flex-col gap-3"
      >
        <label className={labelClass()}>
          <span className="sr-only">First name</span>
          <input
            type="text"
            name="firstName"
            value={first}
            onChange={(event) => setFirst(event.target.value)}
            placeholder="First name"
            autoFocus
            autoComplete="given-name"
            className={inputClass}
          />
        </label>

        <label className={labelClass()}>
          <span className="sr-only">Last name</span>
          <input
            type="text"
            name="lastName"
            value={last}
            onChange={(event) => setLast(event.target.value)}
            placeholder="Last name (optional)"
            autoComplete="family-name"
            className={inputClass}
          />
        </label>
      </form>
    </StepShell>
  );
}
